import { useState } from "react";
import { Link } from "react-router-dom";
import { CreateParticipantInput } from "@ndis/contracts";
import { api, type Participant } from "../lib/api";
import { useFetch } from "../lib/useFetch";
import ImportParticipants from "../components/ImportParticipants";

const statusStyle: Record<string, string> = {
  referral: "bg-slate-100 text-body",
  prospective: "bg-amber-50 text-amber-700",
  onboarded: "bg-teal-50 text-teal-700",
  inactive: "bg-slate-100 text-faint",
};

export default function Participants() {
  const { data, error, loading, reload } = useFetch<Participant[]>("/v1/participants");
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", ndisNumber: "" });
  const [formError, setFormError] = useState<string | null>(null);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    const parsed = CreateParticipantInput.safeParse({
      firstName: form.firstName,
      lastName: form.lastName,
      ndisNumber: form.ndisNumber || undefined,
    });
    if (!parsed.success) {
      // Name the field. "Invalid" on its own gives no clue which input to fix.
      const labels: Record<string, string> = {
        firstName: "First name", lastName: "Last name", ndisNumber: "NDIS number",
      };
      setFormError(
        parsed.error.issues
          .map((issue) => {
            const field = labels[String(issue.path[0])] ?? String(issue.path[0] ?? "");
            return field ? `${field}: ${issue.message}` : issue.message;
          })
          .join("; ") || "Check the fields",
      );
      return;
    }
    try {
      await api("/v1/participants", { method: "POST", body: JSON.stringify(parsed.data) });
      setForm({ firstName: "", lastName: "", ndisNumber: "" });
      setShowForm(false);
      setFormError(null);
      reload();
    } catch (err) {
      setFormError((err as Error).message);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-ink">Participants</h1>
        <div className="flex gap-2">
          <button onClick={() => setShowImport((v) => !v)} className="btn-secondary px-3 py-1.5">
            {showImport ? "Close import" : "Import CSV"}
          </button>
          <button onClick={() => setShowForm((v) => !v)} className="btn-primary">
            {showForm ? "Cancel" : "Add participant"}
          </button>
        </div>
      </div>

      {showImport && <ImportParticipants onImported={reload} />}

      {showForm && (
        <form onSubmit={create} className="card p-5 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <label className="block">
            <span className="label">First name</span>
            <input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })}
              className="input" />
          </label>
          <label className="block">
            <span className="label">Last name</span>
            <input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })}
              className="input" />
          </label>
          <label className="block">
            <span className="label">NDIS number</span>
            <input value={form.ndisNumber} onChange={(e) => setForm({ ...form, ndisNumber: e.target.value })}
              placeholder="43xxxxxxx"
              className="input" />
          </label>
          <button className="btn-dark">Save</button>
          {formError && <p className="text-sm text-red-600 sm:col-span-4">{formError}</p>}
        </form>
      )}

      {loading && <div className="space-y-3"><div className="skeleton h-24" /><div className="skeleton h-24" /></div>}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {data && data.length === 0 && !showForm && (
        <div className="card p-10 text-center">
          <p className="font-medium text-ink">Add your first participant</p>
          <p className="text-sm text-mut">Referrals, onboarding, and care plans all start here.</p>
        </div>
      )}
      {data && data.length > 0 && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-edge-soft">
                <th className="th">Name</th>
                <th className="th">NDIS number</th>
                <th className="th">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.map((p) => (
                <tr key={p.id} className="border-b border-edge-soft last:border-0 hover:bg-hover">
                  <td className="px-5 py-3">
                    <Link to={`/participants/${p.id}`} className="font-medium text-ink hover:text-teal-700">
                      {p.firstName} {p.lastName}
                    </Link>
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-mut">{p.ndisNumber ?? "—"}</td>
                  <td className="px-5 py-3">
                    <span className={`chip ${statusStyle[p.status] ?? statusStyle.referral}`}>
                      {p.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
